import { useRef, useState, useEffect } from "react";
import iconUpload from "../../assets/icon-upload.svg";
import iconClose from "../../assets/icon-close.svg";

interface SignatureUploadProps {
  file: File | null;
  existingUrl?: string | null;
  onChange: (file: File | null) => void;
  error?: string;
}

export function SignatureUpload({ file, existingUrl, onChange, error }: Readonly<SignatureUploadProps>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [typeError, setTypeError] = useState<string | null>(null);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }

    setPreviewUrl(null);
  }, [file]);

  const hasSignature = file || existingUrl;

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.type !== "image/svg+xml") {
      setTypeError("Only SVG files are accepted");
      return;
    }
    setTypeError(null);
    onChange(selected);
  }

  function handleRemove() {
    onChange(null);
    setTypeError(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  const displayError = error || typeError;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2 items-center">
        {hasSignature ? (
          <>
            <div className="size-16 rounded-2xl border border-stroke-primary flex items-center justify-center overflow-hidden bg-bg-primary">
              <img
                src={previewUrl ?? (existingUrl ?? "")}
                alt="Signature"
                className="max-w-full max-h-full object-contain"
              />
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="shrink-0"
              aria-label="Remove signature"
            >
              <img src={iconClose} alt="" width={16} height={16} />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="size-16 rounded-2xl border border-stroke-primary flex items-center justify-center hover:bg-bg-secondary transition-colors"
            aria-label="Upload signature"
          >
            <img src={iconUpload} alt="" width={24} height={24} />
          </button>
        )}
        <span className="text-sm text-text-tertiary">
          Upload Doctor's Signature. SVG format only
        </span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".svg,image/svg+xml"
        onChange={handleFileChange}
        className="hidden"
      />
      {displayError && (
        <p className="text-xs font-medium text-input-hint-error">{displayError}</p>
      )}
    </div>
  );
}
